import { useEffect, useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import {
  ArrowLeft,
  User,
  FileText,
  CreditCard,
  Save,
  Calendar,
  Phone,
  MessageCircle,
  Pencil,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  usePolicyQuery,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
} from '@features/policies/hooks/usePoliciesQuery.js';
import { useEnquiryQuery } from '@features/enquiries/hooks/useEnquiriesQuery.js';
import { useFindClientQuery } from '@features/policies/hooks/useClientsSearchQuery.js';
import { POLICY_TYPE_LABELS, RENEWAL_STATUS_LABELS } from '@repo/constants';
import { daysToExpiry, formatDate, initials, urgencyBucket, daysLabel } from '@repo/utils';
import type { PolicyFormData } from '@features/policies/types/index.js';
import PageLoader from '@components/ui/PageLoader.js';
import Badge from '@components/ui/Badge.js';
import ClientSearch from '@components/ui/ClientSearch.js';
import Button from '@components/ui/Button.js';
import Input from '@components/ui/Input.js';
import Select from '@components/ui/Select.js';
import { cn } from '@utils/Cn.js';

const policySchema = z
  .object({
    insuredName: z.string().min(1, 'Insured name is required'),
    mobileNumber: z.string().refine((v) => v === '' || /^\d{10}$/.test(v.replace(/\D/g, '')), {
      message: 'Enter a valid 10-digit mobile number',
    }),
    referenceNote: z.string(),
    policyType: z.string().min(1, 'Policy type is required'),
    vehicleNumber: z.string(),
    policyNumber: z.string(),
    endDate: z.string().min(1, 'Renewal end date is required'),
    premiumPrice: z.string(),
    paymentLink: z.string(),
    renewalNotice: z.string(),
  })
  .refine((data) => !(data.policyType === 'MOTOR' && !data.vehicleNumber.trim()), {
    message: 'Vehicle number is required for motor policies',
    path: ['vehicleNumber'],
  });

type FormValues = z.infer<typeof policySchema>;

const ALL_TYPE_OPTIONS = Object.entries(POLICY_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};
const initialsColors = [
  'bg-sky-100 text-sky-700 dark:bg-sky-950/45 dark:text-sky-300 border-sky-200/40',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/45 dark:text-emerald-300 border-emerald-200/40',
  'bg-amber-100 text-amber-700 dark:bg-amber-950/45 dark:text-amber-300 border-amber-200/40',
  'bg-rose-100 text-rose-700 dark:bg-rose-950/45 dark:text-rose-300 border-rose-200/40',
  'bg-violet-100 text-violet-700 dark:bg-violet-950/45 dark:text-violet-300 border-violet-200/40',
  'bg-teal-100 text-teal-700 dark:bg-teal-950/45 dark:text-teal-300 border-teal-200/40',
];

function getInitialsColor(name: string) {
  const sum = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return initialsColors[sum % initialsColors.length] ?? initialsColors[0] ?? '';
}

function statusTone(status: string): 'pending' | 'reminded' | 'renewed' | 'notRenewed' | 'lapsed' {
  if (status === 'PENDING') return 'pending';
  if (status === 'REMINDED') return 'reminded';
  if (status === 'RENEWED') return 'renewed';
  if (status === 'NOT_RENEWED') return 'notRenewed';
  return 'lapsed';
}

export default function PolicyFormPage() {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const location = useLocation<{ from?: string } | null>();
  const isEdit = !!id;

  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedClientObj, setSelectedClientObj] = useState<{
    id: string;
    insuredName: string;
    mobileNumber: string | null;
  } | null>(null);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { data, isLoading } = usePolicyQuery(id ?? '');
  const createMutation = useCreatePolicyMutation();
  const updateMutation = useUpdatePolicyMutation();

  const queryParams = new URLSearchParams(location.search);
  const enquiryId = queryParams.get('enquiryId');
  const isConvertMode = !isEdit && !!enquiryId;
  const { data: enquiryData, isLoading: isEnquiryLoading } = useEnquiryQuery(enquiryId ?? '');
  const enquiry = enquiryData?.data;

  // Reset all client-link state when converting a different enquiry.
  // Ionic's page stack keeps the component alive, so stale state from a
  // previous conversion would otherwise bleed through until the new lookup
  // resolves.
  useEffect(() => {
    if (enquiryId) {
      setSelectedClientId(null);
      setSelectedClientObj(null);
    }
  }, [enquiryId]);

  // Derive lookup params — mobile stored without +91 prefix in enquiry
  const enquiryMobile = enquiry?.mobileNumber ?? '';
  const enquiryName = enquiry?.name ?? '';

  // Exact-match lookup: enabled once enquiry data is loaded in convert mode
  // Strip only the leading "+" if present.  Do NOT strip digits — the backend
  // normalises by extracting the last 10 digits for a safe endsWith match.
  const normalizedMobile = enquiryMobile.replace(/^\+/, '');
  const { data: foundClient, isLoading: isClientLookupLoading } = useFindClientQuery(
    normalizedMobile,
    enquiryName,
    isConvertMode && !isEnquiryLoading && (normalizedMobile.length > 0 || enquiryName.length > 0),
  );

  const existing = data?.data;

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(policySchema),
    defaultValues: {
      insuredName: '',
      mobileNumber: '',
      referenceNote: '',
      policyType: 'MOTOR',
      vehicleNumber: '',
      policyNumber: '',
      endDate: '',
      premiumPrice: '',
      paymentLink: '',
      renewalNotice: '',
    },
  });

  // Live watch values for card preview
  const watchInsuredName = watch('insuredName') || '';
  const watchPolicyType = watch('policyType') || 'MOTOR';
  const watchVehicleNumber = watch('vehicleNumber') || '';
  const watchEndDate = watch('endDate') || '';
  const watchReferenceNote = watch('referenceNote') || '';
  const watchMobileNumber = watch('mobileNumber') || '';

  const previewInitials = watchInsuredName ? initials(watchInsuredName) : '??';
  const previewDays = watchEndDate ? daysToExpiry(watchEndDate) : null;
  const previewUrgency = previewDays !== null ? urgencyBucket(previewDays) : 'future';
  const previewDaysLabel = previewDays !== null ? daysLabel(previewDays) : 'Select end date';
  const previewStatus = existing?.renewalStatus ?? 'PENDING';

  // Weighted progress calculation for Add Mode
  const requiredFields = ['insuredName', 'policyType', 'endDate'];
  if (watchPolicyType === 'MOTOR') requiredFields.push('vehicleNumber');

  const optionalFields = [
    'mobileNumber',
    'referenceNote',
    'policyNumber',
    'premiumPrice',
    'paymentLink',
    'renewalNotice',
  ];

  const filledRequired = requiredFields.filter((f) => !!watch(f as keyof FormValues)).length;
  const filledOptional = optionalFields.filter((f) => !!watch(f as keyof FormValues)).length;

  const progressPercentage = Math.round(
    (requiredFields.length > 0 ? (filledRequired / requiredFields.length) * 70 : 70) +
    (optionalFields.length > 0 ? (filledOptional / optionalFields.length) * 30 : 30),
  );

  // Urgency logic for Edit Mode (based on existing policy status)
  const days = existing ? daysToExpiry(existing.endDate) : 0;
  const urgency = existing ? urgencyBucket(days) : 'future';

  const urgencyBannerClass = existing
    ? {
      overdue: 'border-rose-500/15 bg-rose-500/[0.04]',
      due7: 'border-amber-500/15 bg-amber-500/[0.04]',
      due30: 'border-emerald-500/15 bg-emerald-500/[0.04]',
      future: 'border-slate-500/15 bg-slate-500/[0.04]',
    }[urgency]
    : '';

  const urgencyGradient = existing
    ? {
      overdue: 'from-rose-500/10 via-rose-500/[0.02] to-transparent',
      due7: 'from-amber-500/10 via-amber-500/[0.02] to-transparent',
      due30: 'from-emerald-500/10 via-emerald-500/[0.02] to-transparent',
      future: 'from-slate-500/10 via-slate-500/[0.02] to-transparent',
    }[urgency]
    : '';

  const avatarGradient = existing
    ? {
      overdue: 'from-rose-500 to-red-600 text-white shadow-rose-500/20',
      due7: 'from-amber-500 to-yellow-600 text-white shadow-amber-500/20',
      due30: 'from-emerald-500 to-teal-600 text-white shadow-emerald-500/20',
      future: 'from-slate-500 to-slate-600 text-white shadow-slate-500/20',
    }[urgency]
    : '';

  const badgeStyle = existing
    ? {
      overdue:
        'bg-rose-500/10 text-rose-700 border-rose-500/20 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-900/50',
      due7: 'bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-900/50',
      due30:
        'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-900/50',
      future:
        'bg-slate-500/10 text-slate-700 border-slate-500/20 dark:text-slate-300 dark:bg-slate-950/40 dark:border-slate-900/50',
    }[urgency]
    : '';

  const ambientGlowColor = existing
    ? {
      overdue: 'bg-rose-500/10',
      due7: 'bg-amber-500/10',
      due30: 'bg-emerald-500/10',
      future: 'bg-slate-500/10',
    }[urgency]
    : 'bg-indigo-500/10';

  // Auto-link found client when converting an enquiry.
  // IMPORTANT: always update (or clear) the selection once the lookup resolves so that
  // stale state from a previous conversion doesn't bleed through when Ionic keeps the
  // page instance alive in the DOM.
  useEffect(() => {
    if (!isConvertMode || isClientLookupLoading) return;
    if (foundClient) {
      setSelectedClientId(foundClient.id);
      setSelectedClientObj({
        id: foundClient.id,
        insuredName: foundClient.insuredName,
        mobileNumber: foundClient.mobileNumber,
      });
    } else {
      // No existing client found for this enquiry — clear any stale selection
      setSelectedClientId(null);
      setSelectedClientObj(null);
    }
  }, [foundClient, isConvertMode, isClientLookupLoading]);

  useEffect(() => {
    if (existing) {
      reset({
        insuredName: existing.client.insuredName,
        mobileNumber: existing.client.mobileNumber
          ? existing.client.mobileNumber.replace('+91', '')
          : '',
        referenceNote: existing.referenceNote ?? '',
        policyType: existing.policyType,
        vehicleNumber: existing.vehicleNumber ?? '',
        policyNumber: existing.policyNumber ?? '',
        endDate: existing.endDate ? existing.endDate.slice(0, 10) : '',
        premiumPrice: existing.premiumPrice != null ? String(existing.premiumPrice) : '',
        paymentLink: existing.paymentLink ?? '',
        renewalNotice: existing.renewalNotice ?? '',
      });
    } else if (enquiry && !isEdit && !isClientLookupLoading) {
      // Populate name/mobile from found client if available, otherwise from enquiry
      const clientName = foundClient?.insuredName ?? enquiry.name;
      const clientMobile =
        foundClient?.mobileNumber ?? enquiry.mobileNumber;
      reset({
        insuredName: clientName,
        mobileNumber: clientMobile.replace('+91', ''),
        referenceNote: enquiry.referredBy ? `Referred by: ${enquiry.referredBy}` : '',
        policyType: enquiry.policyType,
        vehicleNumber: '',
        policyNumber: '',
        endDate: '',
        premiumPrice: '',
        paymentLink: '',
        renewalNotice: '',
      });
    }
  }, [existing, enquiry, isEdit, reset, foundClient, isClientLookupLoading]);

  useEffect(() => {
    if (existing?.client) {
      setSelectedClientId(existing.client.id);
      setSelectedClientObj(existing.client);
    }
  }, [existing]);

  // Show loader while editing (missing policy data) or while looking up a client on convert
  if (isEdit && (isLoading || !existing)) {
    return <PageLoader message="Loading renewal editor" />;
  }

  if (isConvertMode && (isEnquiryLoading || isClientLookupLoading)) {
    return <PageLoader message="Checking client records..." />;
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  function onSubmit(form: FormValues) {
    const mn = form.mobileNumber.trim();
    const payload: Record<string, unknown> = {
      insuredName: form.insuredName.trim(),
      policyType: form.policyType,
      endDate: form.endDate,
    };
    if (selectedClientId) payload.clientId = selectedClientId;
    if (mn) payload.mobileNumber = `+91${mn.replace(/\D/g, '')}`;
    if (form.referenceNote.trim()) payload.referenceNote = form.referenceNote.trim();
    if (form.policyType === 'MOTOR')
      payload.vehicleNumber = form.vehicleNumber.trim().toUpperCase();
    if (form.policyNumber.trim()) payload.policyNumber = form.policyNumber.trim();
    if (form.premiumPrice) payload.premiumPrice = Number(form.premiumPrice);
    if (form.paymentLink.trim()) payload.paymentLink = form.paymentLink.trim();
    if (form.renewalNotice.trim()) payload.renewalNotice = form.renewalNotice.trim();
    if (enquiryId) payload.enquiryId = enquiryId;

    if (isEdit) {
      updateMutation.mutate(
        { id: id, data: payload },
        {
          onSuccess: () => {
            toast.success('Policy updated successfully');
            if (location.state?.from) {
              history.goBack();
            } else {
              history.replace(`/policies/${id}`);
            }
          },
          onError: () => {
            toast.error('Failed to save changes. Please try again.');
          },
        },
      );
    } else {
      createMutation.mutate(payload as unknown as PolicyFormData, {
        onSuccess: (res) => {
          toast.success('Policy created successfully');
          history.replace(`/policies/${(res as unknown as { data: { id: string } }).data.id}`);
        },
        onError: () => {
          toast.error('Failed to create policy. Please try again.');
        },
      });
    }
  }

  return (
    <IonPage>
      <IonContent className="ion-no-padding" scrollY={!isDesktop}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="h-full min-h-full bg-body-bg lg:overflow-hidden lg:flex lg:flex-col"
        >
          {/* ── Sticky dynamic form header ── */}
          {isEdit ? (
            /* ── Rich Edit Form Header ── */
            <div
              className={cn(
                'border-b px-4 sm:px-6 lg:px-8 pt-5 pb-6 relative overflow-hidden backdrop-blur-md sticky top-0 z-20',
                urgencyBannerClass,
                urgencyGradient,
              )}
            >
              {/* Ambient Background Light Glow */}
              <div
                className={cn(
                  'absolute -top-12 -left-12 w-64 h-64 rounded-full blur-3xl opacity-60 pointer-events-none',
                  ambientGlowColor,
                )}
              />

              <div className="max-w-6xl mx-auto relative z-10">
                {/* Breadcrumb — desktop */}
                <nav className="hidden md:flex items-center gap-1.5 text-[11px] font-semibold text-ink-faint opacity-70 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      history.push('/policies');
                    }}
                    className="hover:text-slate transition-colors cursor-pointer"
                  >
                    Renewals
                  </button>
                  <span>›</span>
                  <button
                    type="button"
                    onClick={() => {
                      history.push(`/policies/${id}`);
                    }}
                    className="hover:text-slate transition-colors cursor-pointer"
                  >
                    {existing?.client.insuredName}
                  </button>
                  <span>›</span>
                  <span className="text-ink font-bold">Edit Details</span>
                </nav>

                {/* Action / Title layout */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0 font-sans">
                    {/* Avatar + Title Container */}
                    <div className="flex items-center gap-3 min-w-0">
                      <motion.div
                        whileHover={{ scale: 1.03 }}
                        className={cn(
                          'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr font-black text-sm sm:text-base shadow-md select-none border border-white/20 transition-all',
                          avatarGradient,
                        )}
                      >
                        {previewInitials}
                      </motion.div>

                      <div className="text-left min-w-0">
                        <h2 className="text-lg sm:text-xl md:text-2xl font-black text-ink leading-tight tracking-tight truncate">
                          Edit: {watchInsuredName || existing?.client.insuredName}
                        </h2>

                        {/* Info tags inside header */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-slate/30 bg-slate/10 text-slate dark:bg-slate/20 backdrop-blur-sm shadow-sm">
                            <Pencil size={11} className="animate-pulse shrink-0" />
                            Editing Registry
                          </span>

                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border backdrop-blur-sm shadow-sm',
                              badgeStyle,
                            )}
                          >
                            {RENEWAL_STATUS_LABELS[previewStatus] ?? previewStatus}
                          </span>

                          {watchPolicyType && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-line bg-surface/50 text-ink-soft backdrop-blur-sm shadow-sm">
                              {POLICY_TYPE_LABELS[watchPolicyType] ?? watchPolicyType}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Back button — right-aligned, matches enquiry form actions slot */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1.5"
                      onClick={() => {
                        history.goBack();
                      }}
                    >
                      <ArrowLeft size={14} />
                      <span>Back</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ── Rich Add Form Header ── */
            <div
              className={cn(
                'border-b px-4 sm:px-6 lg:px-8 pt-5 pb-6 relative overflow-hidden backdrop-blur-md sticky top-0 z-20 border-indigo-500/15 bg-indigo-500/[0.03] from-indigo-500/10 via-indigo-500/[0.02] to-transparent',
              )}
            >
              {/* Ambient Background Light Glow */}
              <div
                className={cn(
                  'absolute -top-12 -left-12 w-64 h-64 rounded-full blur-3xl opacity-60 pointer-events-none',
                  ambientGlowColor,
                )}
              />

              <div className="max-w-6xl mx-auto relative z-10">
                {/* Breadcrumb — desktop */}
                <nav className="hidden md:flex items-center gap-1.5 text-[11px] font-semibold text-ink-faint opacity-70 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      history.push('/policies');
                    }}
                    className="hover:text-slate transition-colors cursor-pointer"
                  >
                    Renewals
                  </button>
                  <span>›</span>
                  <span className="text-ink font-bold">Add New Policy</span>
                </nav>

                {/* Action / Title layout */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0 font-sans">
                    {/* Avatar + Title Container */}
                    <div className="flex items-center gap-3 min-w-0">
                      <motion.div
                        whileHover={{ scale: 1.03 }}
                        className={cn(
                          'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl font-black text-sm sm:text-base shadow-md select-none border border-white/20 transition-all',
                          watchInsuredName
                            ? getInitialsColor(watchInsuredName)
                            : 'bg-gradient-to-tr from-slate-400 to-slate-500 text-white shadow-slate-500/20',
                        )}
                      >
                        {previewInitials}
                      </motion.div>

                      <div className="text-left min-w-0">
                        <h2 className="text-lg sm:text-xl md:text-2xl font-black text-ink leading-tight tracking-tight truncate">
                          {watchInsuredName ? `New: ${watchInsuredName}` : 'Register New Renewal'}
                        </h2>

                        {/* Info tags and live progress bar inside header */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-indigo-500/20 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 dark:bg-indigo-950/40 dark:border-indigo-900/50 backdrop-blur-sm shadow-sm">
                            <Sparkles size={11} className="shrink-0 text-indigo-500" />
                            Drafting Registry
                          </span>

                          {watchPolicyType && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-line bg-surface/50 text-ink-soft backdrop-blur-sm shadow-sm">
                              {POLICY_TYPE_LABELS[watchPolicyType] ?? watchPolicyType}
                            </span>
                          )}

                          {watchPolicyType === 'MOTOR' && watchVehicleNumber && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-line bg-surface/50 text-ink-soft backdrop-blur-sm shadow-sm font-mono uppercase">
                              {watchVehicleNumber}
                            </span>
                          )}

                          {watchEndDate && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-line bg-surface/50 text-ink-soft backdrop-blur-sm shadow-sm font-mono">
                              Expires {formatDate(watchEndDate)}
                            </span>
                          )}

                          {/* Tiny progress inline details */}
                          <div className="flex items-center gap-1.5 ml-1">
                            <div className="w-16 bg-line-strong/30 rounded-full h-1 overflow-hidden">
                              <motion.div
                                className="bg-indigo-600 h-full rounded-full"
                                animate={{ width: `${progressPercentage.toString()}%` }}
                                transition={{ duration: 0.2 }}
                              />
                            </div>
                            <span className="text-[9px] font-bold text-ink-faint">
                              {progressPercentage}% filled
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Back button — right-aligned, matches enquiry form actions slot */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1.5"
                      onClick={() => {
                        history.goBack();
                      }}
                    >
                      <ArrowLeft size={14} />
                      <span>Back</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Form body ── */}
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-8 lg:px-10 py-5 pb-18 md:pb-10 lg:pb-6 lg:flex-1 lg:overflow-hidden lg:flex lg:flex-col">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 sm:gap-8 items-start lg:flex-1 lg:overflow-hidden">
              {/* Left Column: Form Fields */}
              <motion.div
                variants={stagger}
                initial="hidden"
                animate="show"
                className="space-y-6 lg:space-y-8 lg:h-full lg:overflow-y-auto lg:pr-4 lg:pb-10"
              >
                {/* 2-column card layout on desktop for Client Search and Client Info + Policy Config */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-start">
                  {/* Link Client + Client Information */}
                  <div className="space-y-4">
                    <ClientSearch
                      selectedClient={selectedClientObj}
                      onSelect={(client) => {
                        if (client) {
                          setSelectedClientId(client.id);
                          setSelectedClientObj(client);
                          setValue('insuredName', client.insuredName);
                          setValue('mobileNumber', client.mobileNumber?.replace('+91', '') ?? '');
                        } else {
                          setSelectedClientId(null);
                          setSelectedClientObj(null);
                          setValue('insuredName', '');
                          setValue('mobileNumber', '');
                        }
                      }}
                      disabled={isEdit && !!selectedClientObj}
                      label={isEdit ? 'Attached Client' : 'Link to Existing Client'}
                    />

                    <section className="bg-surface border border-line rounded-[20px] sm:rounded-[24px] p-5 sm:p-6 shadow-[0_4px_24px_rgba(15,23,42,0.06)] text-left">
                      <h3 className="text-sm font-bold text-ink mb-4 flex items-center gap-2">
                        <User size={14} className="text-slate" />
                        Client Information
                      </h3>

                      <div className="space-y-4">
                        <Input
                          label="Insured Name"
                          required
                          disabled={!isEdit && !!selectedClientId}
                          placeholder="Enter full name (e.g., Arun Kumar)"
                          error={errors.insuredName?.message}
                          {...register('insuredName')}
                        />

                        <div
                          className={
                            !isEdit && selectedClientId ? 'opacity-60 pointer-events-none' : ''
                          }
                        >
                          <label className="text-xs font-semibold text-ink-soft mb-1.5 block">
                            Mobile Number
                          </label>
                          <div className="flex gap-2">
                            <div className="h-11 px-3.5 rounded-xl border border-line-strong flex items-center text-xs font-bold font-mono bg-paper text-ink-soft select-none shrink-0">
                              +91
                            </div>
                            <div className="flex-1">
                              <Input
                                placeholder="Enter 10-digit number"
                                inputMode="numeric"
                                disabled={!isEdit && !!selectedClientId}
                                error={errors.mobileNumber?.message}
                                {...register('mobileNumber')}
                              />
                            </div>
                          </div>
                        </div>

                        <Input
                          label="Reference Note"
                          placeholder="e.g. Agency broker name, family label"
                          {...register('referenceNote')}
                        />
                      </div>
                    </section>
                  </div>

                  {/* Policy Configuration */}
                  <section className="bg-surface border border-line rounded-[20px] sm:rounded-[24px] p-5 sm:p-6 shadow-[0_4px_24px_rgba(15,23,42,0.06)] text-left">
                    <h3 className="text-sm font-bold text-ink mb-4 flex items-center gap-2">
                      <FileText size={14} className="text-slate" />
                      Policy Configuration
                    </h3>

                    <div className="space-y-4">
                      <Controller
                        name="policyType"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label="Policy Type"
                            options={ALL_TYPE_OPTIONS}
                            value={field.value}
                            onValueChange={field.onChange}
                            error={errors.policyType?.message}
                          />
                        )}
                      />

                      {watchPolicyType === 'MOTOR' && (
                        <Input
                          label="Vehicle Number"
                          required
                          placeholder="e.g. KA03HA1234"
                          className="font-mono uppercase"
                          error={errors.vehicleNumber?.message}
                          {...register('vehicleNumber')}
                        />
                      )}

                      <Input
                        label="Policy Number"
                        placeholder="e.g. OG-24-1102-1801-00002"
                        className="font-mono"
                        {...register('policyNumber')}
                      />

                      <Input
                        label="End Date"
                        required
                        type="date"
                        error={errors.endDate?.message}
                        {...register('endDate')}
                      />
                    </div>
                  </section>
                </div>

                {/* Pricing & Notice Details */}
                <section className="bg-surface border border-line rounded-[20px] sm:rounded-[24px] p-5 sm:p-6 shadow-[0_4px_24px_rgba(15,23,42,0.06)] text-left">
                  <h3 className="text-sm font-bold text-ink mb-4 flex items-center gap-2">
                    <CreditCard size={14} className="text-slate" />
                    Pricing & Notice Details
                  </h3>

                  <div className="space-y-4">
                    <Input
                      label="Premium Price (Rs.)"
                      type="number"
                      placeholder="e.g. 15400"
                      {...register('premiumPrice')}
                    />

                    <Input
                      label="Payment Link"
                      placeholder="https://pay.insurer.com/..."
                      {...register('paymentLink')}
                    />

                    <div>
                      <label className="text-xs font-semibold text-ink-soft mb-1.5 block">
                        Renewal Notice Note
                      </label>
                      <textarea
                        className={cn(
                          'w-full min-h-[100px] p-3 rounded-xl border border-line-strong bg-surface font-sans text-sm text-ink outline-none',
                          'focus:border-slate focus:shadow-[0_0_0_3px_rgba(15,118,110,0.15)] transition-all resize-none placeholder:text-ink-faint',
                        )}
                        placeholder="Specify claims details, loyalty discounts, NCB, etc."
                        {...register('renewalNotice')}
                      />
                    </div>
                  </div>
                </section>

                {/* Form action buttons — matches Enquiry form pattern */}
                <div className="hidden md:flex gap-3 pt-4 border-t border-line">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      history.goBack();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    className="flex-1 flex items-center justify-center gap-1.5 whitespace-nowrap"
                    loading={isSaving}
                    onClick={() => {
                      void handleSubmit(onSubmit)();
                    }}
                  >
                    <Save size={15} />
                    <span>{isEdit ? 'Update' : 'Save'}</span>
                  </Button>
                </div>
              </motion.div>

              {/* Right Column: Sticky Live Preview */}
              <div className="hidden lg:block space-y-4 text-left">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-ink-faint pl-1">
                  Card Live Preview
                </p>

                <div className="group overflow-hidden rounded-[28px] border border-line bg-surface/90 shadow-[0_12px_40px_rgba(15,118,110,0.04)] transition-all duration-300">
                  <div className="flex h-full items-stretch">
                    {/* Urgency edge */}
                    <div
                      className={cn(
                        'w-1.5 flex-none transition-all duration-300 group-hover:w-2',
                        previewUrgency === 'overdue' && 'bg-red-edge',
                        previewUrgency === 'due7' && 'bg-amber-edge',
                        previewUrgency === 'due30' && 'bg-green-edge',
                        previewUrgency === 'future' && 'bg-gray-edge',
                      )}
                    />

                    <div className="flex min-w-0 flex-1 flex-col justify-between">
                      <div className="p-5 flex flex-col gap-3">
                        {/* Name + Avatar Block */}
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              'flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl border text-xs font-bold shadow-sm',
                              getInitialsColor(watchInsuredName || 'Client Name'),
                            )}
                          >
                            {previewInitials}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-base font-extrabold tracking-tight text-ink break-words leading-snug">
                              {watchInsuredName || 'Client Name'}
                            </h3>
                            <p
                              className={cn(
                                'mt-0.5 text-xs font-bold leading-normal',
                                previewUrgency === 'overdue' && 'text-red-fg',
                                previewUrgency === 'due7' && 'text-amber-fg',
                                previewUrgency === 'due30' && 'text-green-fg',
                                previewUrgency === 'future' && 'text-ink-faint',
                              )}
                            >
                              {previewDaysLabel}
                            </p>
                          </div>
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                          <Badge tone="neutral">
                            {POLICY_TYPE_LABELS[watchPolicyType] ?? watchPolicyType}
                          </Badge>
                          {watchVehicleNumber && (
                            <Badge
                              tone="neutral"
                              className="font-mono normal-case tracking-[0.08em]"
                            >
                              {watchVehicleNumber}
                            </Badge>
                          )}
                          <Badge tone={statusTone(previewStatus)} dot>
                            {RENEWAL_STATUS_LABELS[previewStatus] ?? previewStatus}
                          </Badge>
                        </div>

                        {watchReferenceNote && (
                          <div className="rounded-xl bg-paper/60 border border-line/45 px-3 py-2 text-xs text-ink-soft">
                            <span className="text-[9px] font-black tracking-wider text-ink-faint mr-1.5 uppercase">
                              REF:
                            </span>
                            <span className="font-semibold text-ink">
                              {watchReferenceNote.toLowerCase().startsWith('ref:')
                                ? watchReferenceNote.slice(4).trim()
                                : watchReferenceNote}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Footer row */}
                      <div className="flex items-center justify-between border-t border-line/80 px-5 py-3 bg-surface/30">
                        <div className="flex items-center gap-1.5 text-ink-faint">
                          <Calendar size={12} className="shrink-0 text-ink-faint/70" />
                          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] sm:tracking-[0.15em]">
                            {watchEndDate ? `Ends ${formatDate(watchEndDate)}` : 'Ends ———'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          {watchMobileNumber && (
                            <>
                              <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-line-strong bg-surface text-ink-soft shadow-sm opacity-50">
                                <Phone size={13} />
                              </div>
                              <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-green-edge/30 bg-green-bg text-green-fg shadow-sm opacity-50">
                                <MessageCircle size={13} />
                              </div>
                            </>
                          )}
                          <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-line-strong bg-surface text-ink-soft shadow-sm opacity-50">
                            <Pencil size={12} />
                          </div>
                          <div className="flex h-5 w-5 items-center justify-center text-ink-faint">
                            <ChevronRight size={14} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Sticky bottom save bar — mobile only ── */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-line bg-surface/95 backdrop-blur-xl px-4 py-3 pb-safe">
            <div className="flex gap-2.5 max-w-6xl mx-auto">
              <Button
                variant="outline"
                size="lg"
                className="flex-1"
                onClick={() => {
                  history.goBack();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="lg"
                loading={isSaving}
                leftIcon={<Save size={15} />}
                className="flex-1 whitespace-nowrap"
                onClick={() => {
                  void handleSubmit(onSubmit)();
                }}
              >
                {isEdit ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        </motion.div>
      </IonContent>
    </IonPage>
  );
}
